import React, {
  useEffect,
  useState
} from 'react';
import './AddDocumentRegistry.css';
import axiosInstance from '../../../Config/axiosInstance';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDropzone } from 'react-dropzone';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';



function AddDocumentRegistry() {
  // Stări pentru datele formularului, fișiere, erori și mesaje de confirmare
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    direction: 'output',
    sender: '',
    recipient: '',
    content: ''
  });
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropzoneKey, setDropzoneKey] = useState(Date.now());
  const [fileAddedMessage, setFileAddedMessage] = useState('');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [errorNotification, setErrorNotification] = useState('');
  const {
    getRootProps,
    getInputProps,
    isDragActive
  } = useDropzone({
    accept: {
      'application/pdf': [],
      'application/msword': [],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
      'image/jpeg': [],
      'image/png': []
    },
    multiple: false,
    maxSize: 1024 * 1024 * 1024, // 1GB
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        setFiles(acceptedFiles);
        setFileAddedMessage(`File "${acceptedFiles[0].name}" added successfully!`);

        const file = acceptedFiles[0];
        if (file.type === 'application/pdf') {
          const url = URL.createObjectURL(file);
          setPdfPreviewUrl(url);
        } else {
          setPdfPreviewUrl(null);
        }

        setTimeout(() => {
          setFileAddedMessage('');
        }, 3000);
      }
    }
  });

  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('organization');

  useEffect(() => {
    if (!organizationId) {
      navigate('/document-registry-dashboard', {
        replace: true,
      });
    }
  }, [organizationId, navigate]);

  const showErrorNotification = (message) => {
    setErrorNotification(message);
    setTimeout(() => {
      setErrorNotification('');
    }, 3000);
  };

  // Actualizează datele din formular
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Actualizează direcția (input/output)
  const handleRadioChange = (e) => {
    setFormData(prev => ({ ...prev, direction: e.target.value }));
  };

  const handleRemoveFile = (indexToRemove) => {
    setFiles(prevFiles => {
      const updatedFiles = prevFiles.filter((_, index) => index !== indexToRemove);
      if (updatedFiles.length === 0) {
        setDropzoneKey(Date.now());
      }
      return updatedFiles;
    });
  };

  // Trimite formularul către API
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    setIsSubmitting(true);

    const newErrors = {};
    if (!formData.sender) newErrors.sender = "Sender is required";
    if (!formData.recipient) newErrors.recipient = "Recipient is required";
    if (!formData.content) newErrors.content = "Content is required";

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      setIsSubmitting(false);
      return;
    }

    try {
      const submissionData = new FormData();

      submissionData.append('direction', formData.direction);
      submissionData.append('sender', formData.sender);
      submissionData.append('recipient', formData.recipient);
      submissionData.append('content', formData.content);

      submissionData.append('organization', organizationId);

      if (files.length > 0) {
        submissionData.append('attachments', files[0]);
      }

      const response = await axiosInstance.post("/document-registry/", submissionData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log("Added successfully:", response.data);
      setShowSuccessMessage(true);

      setTimeout(() => {
        setShowSuccessMessage(false);

        navigate(
          `/document-registry?organization=${organizationId}`,
          { replace: true }
        );
      }, 2000);

    } catch (error) {
      console.error("Error adding:", error.response?.data || error.message);
      setIsSubmitting(false);
    }
  };

  // Afișează un icon în funcție de tipul fișierului (PDF, DOC, etc.)
  const getPreviewIcon = (fileObject) => {
    const { file } = fileObject;
    const fileType = file.type;

    let icon;
    if (fileType === 'application/pdf') {
      icon = <PictureAsPdfIcon style={{ fontSize: 40, color: '#d32f2f' }} />;
    } else if (
      fileType === 'application/msword' ||
      fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      icon = <DescriptionIcon style={{ fontSize: 40, color: '#1976d2' }} />;
    } else {
      icon = <InsertDriveFileIcon style={{ fontSize: 40, color: '#757575' }} />;
    }
    return (
      <div title={file.name}>
        {icon}
      </div>
    );
  };

  return (
    <div className="add_document_registry_page">

      {errorNotification && (
        <div className="error-notification">
          {errorNotification}
        </div>
      )}

      {/* Afișează mesaj de succes după salvare */}
      {showSuccessMessage && (
        <div className="success-snackbar">Document added successfully!</div>
      )}

      {fileAddedMessage && (
        <div className="success-snackbar">{fileAddedMessage}</div>
      )}

      <div className="registration-header">
        {/* Header cu titlu și buton de întoarcere */}
        <ArrowBackIcon className="back-icon" onClick={() => navigate(-1)} />
        <h2>New registration</h2>
      </div>
      <hr className="header-divider" />

      {/* Formularul propriu-zis pentru adăugare document */}
      <div className="registration-card">
        <form onSubmit={handleSubmit} className="registration-form">
          <label className="direction-label">Direction</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="direction"
                value="output"
                checked={formData.direction === 'output'}
                onChange={handleRadioChange}
              />
              Output
            </label>
            <label>
              <input
                type="radio"
                name="direction"
                value="input"
                checked={formData.direction === 'input'}
                onChange={handleRadioChange}
              />
              Input
            </label>
          </div>

          <div className="form-group">
            <label>Sender name <span className="required">*</span></label>
            <input
              type="text"
              name="sender"
              value={formData.sender}
              onChange={handleChange}
              className={errors.sender ? 'input-error' : ''}
              placeholder="Sender"
            />
            {errors.sender && <div className="error-text">{errors.sender}</div>}
          </div>

          <div className="form-group">
            <label>Recipient name <span className="required">*</span></label>
            <input
              type="text"
              name="recipient"
              value={formData.recipient}
              onChange={handleChange}
              className={errors.recipient ? 'input-error' : ''}
              placeholder="Recipient"
            />
            {errors.recipient && <div className="error-text">{errors.recipient}</div>}
          </div>

          <div className="form-group">
            <label>Document content <span className="required">*</span></label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              className={errors.content ? 'input-error' : ''}
              placeholder="Document content"
              rows={4}
            />
            {errors.content && <div className="error-text">{errors.content}</div>}
          </div>

          <div className="form-group">
            <label>Upload document</label>
            <div key={dropzoneKey}>
              <div {...getRootProps({ className: 'dropzone' })}>
                <input {...getInputProps()} />
                <div className="dropzone-content">
                  {isDragActive ? (
                    <p>Drop the file here...</p>
                  ) : (
                    <p>Drag & drop or click to select a file</p>
                  )}
                  <CloudUploadIcon style={{ fontSize: 40, color: '#888' }} />
                </div>

                {files.length > 0 && (
                  <div className="preview-container">
                    {files.map((file, index) => {
                      const isPdf = file.type === 'application/pdf';

                      return (
                        <div
                          key={index}
                          className="file-preview"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isPdf && pdfPreviewUrl) {
                              const newWindow = window.open();
                              newWindow.document.write(`
                                <html>
                                  <head>
                                    <title>${file.name}</title>
                                  </head>
                                  <body style="margin:0">
                                    <embed src="${pdfPreviewUrl}" type="application/pdf" width="100%" height="100%" />
                                  </body>
                                </html>
                              `);
                              newWindow.document.close();
                            } else {
                              showErrorNotification('Preview is only available for PDF files.');
                            }
                          }}

                          style={{ cursor: 'pointer' }}
                          title={isPdf ? 'Click to preview PDF' : 'Preview available only for PDF'}
                        >
                          {getPreviewIcon({ file })}
                          <span className="file-name">{file.name}</span>
                          <button
                            type="button"
                            className="remove-file-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFile(index);
                            }}
                            title="Remove file"
                          >
                            <DeleteIcon fontSize="small" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <p className="file-hint">
              * Accepted file types: PDF, Word (.doc/.docx), JPG, PNG. Only one file allowed.
            </p>
          </div>

          <div className="form-buttons">
            <button type="button" className="discard" onClick={() => navigate(-1)}>
              Discard
            </button>
            <button
              type="submit"
              className="save"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddDocumentRegistry;
